import { MagnifyingGlass } from '@creditornot/cb-icons';
import { Typography as T, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Switch } from '@creditornot/cb-switch';
import React, { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { usePluralization } from 'common/hooks';
import useDebounceString from 'common/hooks/useDebounceString';
import Loading from 'core/Loading';

import AvailabilityStatusFilter from './components/AvailabilityFilter';
import CityFilter from './components/CityFilter';
import EmptyView from './components/EmptyView';
import ErrorView from './components/ErrorView';
import VenueAvailabilityStatusTable from './components/VenueAvailabilityStatusTable';
import { transformVenueDataFromAPI } from './helpers';
import useVenueAvailabilityStatus from './hooks/useVenueAvailabilityStatus';
import useVenueIds from './hooks/useVenueIds';
import { telemetryVenueAvailabilityView } from './telemetry';
import { VenueAvailabilityStatus as AvailabilityStatuses, VenueRow, Entity } from './types';

const Heading = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding-block: 44px 16px;
  padding-inline: 24px;
`;
const FilteringContainer = styled.div`
  align-items: center;
  display: flex;
  padding: 16px 24px;
  gap: 8px;
`;

const StyledSearchInput = styled(Input)`
  width: 280px;
`;

const VenueCount = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const TableContainer = styled.div`
  display: flex;
  flex: 1;
`;

const LoaderContainer = styled.div`
  margin: auto;
`;

const TableHeaderRow = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  padding: 18px 24px;
`;

const TableHeader = styled(T.Overline3)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const SwitchContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  gap: 8px;
`;

type Props = {
  entity: Entity;
  renderHeader?: () => React.ReactNode;
  handleRemoveAction?: (venue: VenueRow) => void;
};
const VenueAvailabilityStatus: FC<Props> = ({ entity, renderHeader, handleRemoveAction }) => {
  useEffect(() => {
    telemetryVenueAvailabilityView();
  }, []);
  const { FormattedPluralMessage } = usePluralization();
  const [searchString, setSearchString] = useState('');
  const [showInactiveVenues, setShowInactiveVenues] = useState(false);
  const debouncedSearchString = useDebounceString(searchString);
  const intl = useIntl();
  const { venueIds, isLoading: isLoadingVenueIds, error: fetchVenueIdError } = useVenueIds(entity);
  const {
    venuesData,
    isLoading: isLoadingVenueAvailabilityStatus,
    error: fetchVenueAvailabilityStatusError,
  } = useVenueAvailabilityStatus(venueIds);
  const isLoading = isLoadingVenueIds || isLoadingVenueAvailabilityStatus;
  const hasError = fetchVenueIdError || fetchVenueAvailabilityStatusError;
  const { venueRowList, availableCities, containsInactiveVenues } = useMemo(
    () => transformVenueDataFromAPI(venuesData || [], showInactiveVenues),
    [showInactiveVenues, venuesData],
  );
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedAvailabilityStatuses, setSelectedAvailabilityStatuses] = useState<AvailabilityStatuses[]>([]);

  const filteredVenues = useMemo(() => {
    const containsSearchStringInVenueNameOrAddress = ({ name, address }: VenueRow) => {
      const searchString = debouncedSearchString.toLowerCase();
      return (
        name.some(({ value }) => value.toLowerCase().includes(searchString)) ||
        address.toLowerCase().includes(searchString)
      );
    };

    const noCityFilterSelected = selectedCities.length === 0;
    const isVenueFromSelectedCities = ({ cityName }: VenueRow) =>
      noCityFilterSelected || selectedCities.includes(cityName);

    const noAvailabilityStatusFilterSelected = selectedAvailabilityStatuses.length === 0;
    const meetsSelectedAvailabilityStatuses = ({ status }: VenueRow) =>
      noAvailabilityStatusFilterSelected || selectedAvailabilityStatuses.includes(status);

    return venueRowList
      .filter(containsSearchStringInVenueNameOrAddress)
      .filter(isVenueFromSelectedCities)
      .filter(meetsSelectedAvailabilityStatuses);
  }, [venueRowList, debouncedSearchString, selectedCities, selectedAvailabilityStatuses]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => setSearchString(e.target.value);

  const noAvailableVenues = filteredVenues.length === 0;
  return (
    <Root>
      {renderHeader ? (
        renderHeader()
      ) : (
        <Heading>
          <T.Heading2>
            <FormattedMessage id="navigation.venue-availability-status" />
          </T.Heading2>
        </Heading>
      )}
      <FilteringContainer>
        <StyledSearchInput
          icon={<MagnifyingGlass />}
          placeholder={intl.formatMessage({ id: 'venue-availability-status.filters.search' })}
          type="text"
          value={searchString}
          onChange={handleSearch}
          onClearClick={() => setSearchString('')}
        />
        <CityFilter
          availableCities={availableCities}
          selectedCities={selectedCities}
          handleSelect={setSelectedCities}
        />
        <AvailabilityStatusFilter
          selectedAvailabilityStatuses={selectedAvailabilityStatuses}
          handleSelect={setSelectedAvailabilityStatuses}
        />
        <VenueCount>
          {filteredVenues.length < venueRowList.length && <>{filteredVenues.length}/</>}
          <FormattedPluralMessage id="venue-availability-status.venue-count" count={venueRowList.length} />
        </VenueCount>
        {containsInactiveVenues ? (
          <SwitchContainer>
            <T.Body3>
              <FormattedMessage id="venue-availability-status.filters.show-inactive-venues" />
            </T.Body3>
            <Switch checked={showInactiveVenues} onChange={() => setShowInactiveVenues(!showInactiveVenues)} />
          </SwitchContainer>
        ) : null}
      </FilteringContainer>
      <TableHeaderRow>
        <TableHeader>
          <FormattedMessage id="venue-availability-status.table.venue" />
        </TableHeader>
        <TableHeader>
          <FormattedMessage id="venue-availability-status.table.venue-status" />
        </TableHeader>
        <TableHeader>
          <FormattedMessage id="venue-availability-status.table.merchant" />
        </TableHeader>
        <TableHeader>
          <FormattedMessage id="venue-availability-status.table.contact" />
        </TableHeader>
      </TableHeaderRow>
      <TableContainer>
        {isLoading ? (
          <LoaderContainer>
            <Loading />
          </LoaderContainer>
        ) : hasError ? (
          <ErrorView />
        ) : noAvailableVenues ? (
          <EmptyView />
        ) : (
          <VenueAvailabilityStatusTable venues={filteredVenues} handleRemoveAction={handleRemoveAction} />
        )}
      </TableContainer>
    </Root>
  );
};

export default VenueAvailabilityStatus;
